import { createContext, useContext, useEffect, useRef } from 'react';
import { IAgencyMetrics } from './IAgencyMetrics';
import { useLocalStorage } from 'Utilities/UseStorage';
import { useUserData } from '../User/UserContext';
import * as defaultAgencyQuoteData from 'Data/defaultAgencyQuoteData.json';
import { IUserData } from 'Foundation/User/UserContextModels';

export type IAgencyMetricsContext = {
  agencyMetrics: IAgencyMetrics | null;
  updateAgencyMetrics: (newMetrics: IAgencyMetrics) => void;
  getAgencyMetrics: (isReset: boolean) => void;
};

const initialState: IAgencyMetricsContext = {
  agencyMetrics: null,
  updateAgencyMetrics: () => {},
  getAgencyMetrics: () => {},
};

export const AgencyMetricsContext = createContext(initialState);
export const placeholderKey = 'AgencyMetrics_PLACEHOLDER';

export function craftStorageKey(userData: IUserData | undefined) {
  if (userData?.selectedStatCodeData?.SubStat) {
    return `${userData.isMockingAgent ? 'mocking_' : ''}AgencyMetrics_${userData.selectedStatCodeData.SubStat}`;
  } else {
    return placeholderKey;
  }
}

export function AgencyMetricsContextProvider({ children }: { children: React.ReactNode }) {
  const { userData } = useUserData();
  const expirationMinutes: number = 240;
  const localStorageKey = craftStorageKey(userData);
  const lastFetchAbortRef = useRef<AbortController | null>(null);
  const [agencyMetrics, setAgencyMetrics] = useLocalStorage<IAgencyMetrics>(
    localStorageKey,
    undefined,
    expirationMinutes,
  );

  const areMetricsEqual = (a: IAgencyMetrics | null | undefined, b: IAgencyMetrics | null | undefined) => {
    if (a === b) return true;
    if (!a || !b) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  const getAgencyMetrics = async (isReset: boolean) => {
    if (lastFetchAbortRef.current) lastFetchAbortRef.current.abort();
    const abortController = new AbortController();
    lastFetchAbortRef.current = abortController;
    let next: IAgencyMetrics | null = null;
    try {
      const response = await fetch(`/api/sitecore/pla360/GetAgencyMetrics`, {
        method: 'POST',
        body: new URLSearchParams({
          isReset: isReset ? 'true' : 'false',
        }),
        signal: abortController.signal,
      });
      if (response.ok) {
        try {
          const data = await response.json();
          if (data?.success && data?.data) {
            next = data.data as IAgencyMetrics;
          } else {
            console.log(
              `Failed Call to GetAgencyMetrics\nSUBSTAT: ${userData?.selectedStatCodeData?.SubStat}\nstatusCode: ${data?.statusCode}\nerror: ${data?.error}`,
            );
            next = {
              ...(defaultAgencyQuoteData as any),
              SUBSTAT: userData?.selectedStatCodeData?.SubStat ?? '',
            } as IAgencyMetrics;
          }
        } catch (err: any) {
          console.error(
            `ERROR parsing GetAgencyMetrics response SUBSTAT: ${userData?.selectedStatCodeData?.SubStat}\n${err}`,
          );
          next = {
            ...(defaultAgencyQuoteData as any),
            SUBSTAT: userData?.selectedStatCodeData?.SubStat ?? '',
          } as IAgencyMetrics;
        }
      } else {
        console.log(
          `Response from Call to GetAgencyMetrics not ok SUBSTAT: ${userData?.selectedStatCodeData?.SubStat}\nstatus: ${response.status}`,
        );
        next = {
          ...(defaultAgencyQuoteData as any),
          SUBSTAT: userData?.selectedStatCodeData?.SubStat ?? '',
        } as IAgencyMetrics;
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return; // ignore aborted
      console.error(`Fetch error GetAgencyMetrics SUBSTAT: ${userData?.selectedStatCodeData?.SubStat}\n${err}`);
      next = {
        ...(defaultAgencyQuoteData as any),
        SUBSTAT: userData?.selectedStatCodeData?.SubStat ?? '',
      } as IAgencyMetrics;
    }
    if (next && !areMetricsEqual(agencyMetrics, next)) {
      setAgencyMetrics({ data: next });
    }
  };

  const updateAgencyMetrics = async (newMetrics: IAgencyMetrics) => {
    try {
      await fetch(`/api/sitecore/pla360/SetAgencyMetrics`, {
        method: 'POST',
        body: new URLSearchParams({
          jsonData: JSON.stringify(newMetrics),
          agencyCode: (agencyMetrics as any)?.SUBSTAT,
        }),
      });
    } catch (err: any) {
      console.error(
        `ERROR in AgencyMetricsContext.updateAgencyMetrics(): SUBSTAT: ${userData?.selectedStatCodeData?.SubStat}\n${err}`,
      );
    } finally {
      if (!areMetricsEqual(agencyMetrics, newMetrics)) {
        setAgencyMetrics({ data: newMetrics });
      }
    }
  };

  useEffect(() => {
    // When key is set to placeholder or a non-mocking value, wipe all previously mocked values
    if (localStorageKey === placeholderKey || !localStorageKey.includes('mocking_')) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('mocking_AgencyMetrics')) {
          localStorage.removeItem(key);
        }
      }
    }
  }, [localStorageKey]);

  useEffect(() => {
    if (userData?.isPL() && agencyMetrics?.SUBSTAT !== userData?.selectedStatCodeData?.SubStat) {
        getAgencyMetrics(false);
    }
  }, []);

  // Cross-tab sync
  useEffect(() => {
    if (localStorageKey === placeholderKey) return;
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== localStorageKey || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        const next = parsed?.data as IAgencyMetrics | undefined;
        if (next && !areMetricsEqual(agencyMetrics, next)) {
          setAgencyMetrics({ data: next });
        }
      } catch (err) {
        console.warn('AgencyMetricsContext: Failed to parse storage event data', err);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageKey, agencyMetrics]);

  let shared: IAgencyMetricsContext = {
    agencyMetrics: agencyMetrics,
    updateAgencyMetrics: (newMetrics: IAgencyMetrics) => updateAgencyMetrics(newMetrics),
    getAgencyMetrics: (isReset: boolean) => getAgencyMetrics(isReset),
  };

  return !agencyMetrics && userData?.isPL() ? (
    <></>
  ) : (
    <AgencyMetricsContext.Provider value={shared}>{children}</AgencyMetricsContext.Provider>
  );
}

export function useAgencyMetrics() {
  const context = useContext(AgencyMetricsContext);
  if (context === undefined) {
    throw new Error('useAgencyMetrics must be used within a AgencyMetricsContextProvider');
  }
  return context;
}
